import photos from './photos.json';
import track from './track.json';

export type VoyagePhoto = { file: string; alt: string; w: number; h: number };
export type VoyageStop = { slug: string; name: string; when: string; from: string; to: string; lat: number; lon: number; boat: 'Itineris' | 'COS'; text: string; photos: VoyagePhoto[] };

/**
 * Where we've sailed since 2021, in order. Dates and positions come from the geotags on our photos
 * (track.json); the words and the photos (photos.json, chosen by Steve) are ours. Itineris was our first boat;
 * COS took over in Grenada.
 */
const STOPS: Omit<VoyageStop, 'from' | 'to' | 'lat' | 'lon' | 'photos'>[] = [
  { slug: 'miami', name: 'Miami, Florida', when: 'Home port, 2021–2022', boat: 'Itineris',
    text: 'Where it started: Itineris, our first boat, at Dinner Key Marina in Coconut Grove, getting ready between trips across the Gulf Stream.' },
  { slug: 'bahamas', name: 'The Bahamas', when: 'Three winters, 2021–2023', boat: 'Itineris',
    text: 'Three winters in the Exumas: George Town and Stocking Island, the swimming pigs of Big Major Cay, Staniel and Pipe Cays, Shroud and Highborne Cays, and finally Conception Island.' },
  { slug: 'turks-caicos', name: 'Turks and Caicos', when: 'January 2023', boat: 'Itineris',
    text: 'Providenciales, a short stop on the way south once we left the Bahamas for good.' },
  { slug: 'puerto-rico', name: 'Puerto Rico and Culebra', when: 'January–February 2023', boat: 'Itineris',
    text: 'Fajardo on Puerto Rico’s east coast, then Culebra and Flamenco Beach.' },
  { slug: 'virgin-islands', name: 'The Virgin Islands', when: 'February–March 2023', boat: 'Itineris',
    text: 'St Thomas and St John, then the British Virgin Islands: Jost Van Dyke, Norman Island, Tortola and the boulders of Virgin Gorda.' },
  { slug: 'leewards', name: 'Anguilla, Sint Maarten and St Barth', when: '2023', boat: 'Itineris',
    text: 'Spring among Anguilla, Sint Maarten and St Barth. Itineris spent the 2023 hurricane season in Sint Maarten.' },
  { slug: 'antigua', name: 'Antigua and Barbuda', when: 'May 2023 and January 2024', boat: 'Itineris',
    text: 'English Harbour and Nelson’s Dockyard, and the long, empty beaches of Barbuda, twice.' },
  { slug: 'grenada', name: 'Grenada and the Grenadines', when: 'February 2024 – February 2025', boat: 'Itineris',
    text: 'Our pitstop: most of 2024 based in Grenada, with trips into the Grenadines. This is where COS took over from Itineris.' },
  { slug: 'bonaire', name: 'Bonaire', when: 'February 2025', boat: 'COS',
    text: 'COS heading west at last: diving off Kralendijk and Klein Bonaire.' },
  { slug: 'panama', name: 'Panama', when: 'March–May 2025', boat: 'COS',
    text: 'The San Blas islands, then Colón, the Panama Canal locks and Panama City, getting ready for the Pacific.' },
  { slug: 'pacific', name: 'Crossing the Pacific', when: 'May 2025', boat: 'COS',
    text: 'Thirty-one days from Panama to the Marquesas, past the Galápagos: our longest passage yet.' },
  { slug: 'marquesas', name: 'Nuku Hiva, Marquesas', when: 'June 2025', boat: 'COS',
    text: 'Landfall in French Polynesia: Taiohae Bay, green valleys and carved tiki.' },
  { slug: 'tuamotus', name: 'The Tuamotus', when: 'June–September 2025', boat: 'COS',
    text: 'The coral atolls: Rangiroa’s Blue Lagoon, Fakarava north and south, and Tahanea.' },
  { slug: 'tahiti', name: 'Tahiti and Moorea', when: 'October 2025 and April 2026', boat: 'COS',
    text: 'Tahiti’s west coast and Moorea’s Cook’s Bay, for provisions, boat work and the mountains.' },
  { slug: 'bora-bora', name: 'Bora Bora', when: 'October 2025 – May 2026', boat: 'COS',
    text: 'We waited out the South Pacific cyclone season inside Bora Bora’s lagoon, under Mount Otemanu.' },
  { slug: 'maupiti', name: 'Maupiti', when: 'May 2026', boat: 'COS',
    text: 'Three weeks in Maupiti’s quiet lagoon before heading west.' },
  { slug: 'fiji', name: 'Fiji', when: 'June–October 2026', boat: 'COS',
    text: 'By way of the Cook Islands and Tonga to Vuda Marina on Viti Levu, our base for the southern winter.' },
  { slug: 'new-caledonia', name: 'Nouméa, New Caledonia', when: 'October 2026', boat: 'COS',
    text: 'Where we are now, getting ready for the next leg west to Vanuatu, the Solomons, Micronesia and Palau.' },
];

export type VoyageCountry = { name: string; first: string; last: string; days?: number; stopped: boolean };
const T = track as unknown as { countries: VoyageCountry[]; nm: number; first: string; track: [number, number][]; stops: Record<string, { from: string; to: string; lat: number; lon: number }> };
const P = photos as unknown as Record<string, VoyagePhoto[]>;

export const VOYAGE = {
  since: Number(T.first.slice(0, 4)),
  /** Measured between the places our photos were taken each day, leaving out flights. */
  nm: T.nm,
  /** The countries and territories Steve lists, in his order (2026-10-20); the three marked stopped: false we only sailed past. */
  countries: T.countries,
  /** Countries and territories we stopped in (Miami, Puerto Rico and the US Virgin Islands count separately). */
  stoppedIn: T.countries.filter(c => c.stopped).length,
  track: T.track,
  stops: STOPS.map(s => {
    const at = T.stops[s.slug];
    if (!at) throw new Error(`No dates for ${s.slug} in track.json`);
    return { ...s, ...at, photos: P[s.slug] ?? [] };
  }),
};
